import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useSesion } from "./composables/useSesion";

/**
 * Tres lugares y nada más: tus tareas, lo que han hecho (el historial) y los
 * ajustes. Todo lo demás cuelga de ahí.
 *
 * Las vistas se cargan en diferido: el primer pintado no espera a pantallas que
 * ni siquiera has abierto.
 */
const routes: RouteRecordRaw[] = [
  // La puerta: la única pantalla que se pinta sin haber entrado.
  {
    path: "/entrar",
    name: "entrar",
    component: () => import("@/views/EntrarView.vue"),
    meta: { publica: true, sinCascaron: true },
  },
  {
    path: "/mi-contrasena",
    name: "cambiar-password",
    component: () => import("@/views/CambiarPasswordView.vue"),
    meta: { conPasswordTemporal: true },
  },

  { path: "/", name: "tareas", component: () => import("@/views/TareasView.vue") },
  { path: "/tareas/nueva", name: "tarea-nueva", component: () => import("@/views/TareaNuevaView.vue") },
  {
    path: "/tareas/:id",
    name: "tarea",
    component: () => import("@/views/TareaView.vue"),
    props: true,
  },

  { path: "/historial", name: "historial", component: () => import("@/views/HistorialView.vue") },
  {
    path: "/corridas/:id",
    name: "corrida",
    component: () => import("@/views/CorridaView.vue"),
    props: true,
  },

  { path: "/ajustes", name: "ajustes", component: () => import("@/views/AjustesView.vue") },

  // Lo del administrador. La guardia de abajo lo esconde y el servidor lo niega.
  {
    path: "/usuarios",
    name: "usuarios",
    component: () => import("@/views/UsuariosView.vue"),
    meta: { soloAdmin: true },
  },
  {
    path: "/bitacora",
    name: "bitacora",
    component: () => import("@/views/BitacoraView.vue"),
    meta: { soloAdmin: true },
  },

  // Las direcciones de Chuy 2 no mueren: llevan a lo que las reemplazó.
  { path: "/objetivos/:rest(.*)*", redirect: "/" },
  { path: "/chambas/:rest(.*)*", redirect: "/historial" },
  { path: "/taller", redirect: "/" },

  { path: "/:pathMatch(.*)*", redirect: "/" },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

/**
 * La guardia de la puerta. No es seguridad —la API revisa la sesión en cada
 * petición—, es comodidad: que nadie llegue a pantallas que van a salir vacías.
 *
 *   1. Sin sesión → a la puerta, recordando a dónde ibas.
 *   2. Con contraseña temporal → a cambiarla, y a ningún otro lado.
 *   3. Sin ser administrador → a las tareas.
 */
router.beforeEach(async (to) => {
  const { sesion, resuelta, cargar, esAdmin, debeCambiarPassword } = useSesion();

  if (!resuelta.value) {
    try {
      await cargar();
    } catch {
      // Si el servidor no contesta, se deja pasar: el tablero enseña su propio
      // aviso de «no alcanzo al servidor», que dice más que una puerta.
      return true;
    }
  }

  if (to.meta["publica"]) return sesion.value ? { name: "tareas" } : true;
  if (!sesion.value) return { name: "entrar", query: { volverA: to.fullPath } };
  if (debeCambiarPassword.value && !to.meta["conPasswordTemporal"]) return { name: "cambiar-password" };
  if (to.meta["soloAdmin"] && !esAdmin.value) return { name: "tareas" };
  return true;
});
